import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const FavoritesContext = createContext(null);

const API = process.env.REACT_APP_SERVER_URL;

export function FavoritesProvider({ user, children }) {
  const [itemIds, setItemIds] = useState(new Set());
  const [productIds, setProductIds] = useState(new Set());
  const [tourIds, setTourIds] = useState(new Set());
  const [serviceIds, setServiceIds] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  const syncFromUser = useCallback((userData) => {
    if (!userData) {
      setItemIds(new Set());
      setProductIds(new Set());
      setTourIds(new Set());
      setServiceIds(new Set());
      return;
    }
    setItemIds(new Set((userData.favorites || []).map(String)));
    setProductIds(new Set((userData.favoriteProducts || []).map(String)));
    setTourIds(new Set((userData.favoriteTours || []).map(String)));
    setServiceIds(new Set((userData.favoriteServices || []).map(String)));
  }, []);

  useEffect(() => {
    syncFromUser(user);
  }, [user, syncFromUser]);

  const requireAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) return true;
    navigate('/login', { state: { redirectTo: `${location.pathname}${location.search}` } });
    return false;
  }, [navigate, location.pathname, location.search]);

  const toggleItem = useCallback(async (itemId) => {
    if (!requireAuth()) return;
    const id = String(itemId);
    const token = localStorage.getItem('token');
    const isFavorite = itemIds.has(id);

    try {
      const res = await fetch(`${API}/api/profile/favorites/${id}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setItemIds(new Set((data.favorites || []).map(String)));
    } catch (err) {
      console.error('Ошибка избранного (аренда):', err);
    }
  }, [itemIds, requireAuth]);

  const toggleProduct = useCallback(async (productId) => {
    if (!requireAuth()) return;
    const id = String(productId);
    const token = localStorage.getItem('token');
    const isFavorite = productIds.has(id);

    try {
      const res = await fetch(`${API}/api/profile/favorite-products/${id}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setProductIds(new Set((data.favoriteProducts || []).map(String)));
    } catch (err) {
      console.error('Ошибка избранного (магазин):', err);
    }
  }, [productIds, requireAuth]);

  const toggleTour = useCallback(async (tourId) => {
    if (!requireAuth()) return;
    const id = String(tourId);
    const token = localStorage.getItem('token');
    const isFavorite = tourIds.has(id);

    try {
      const res = await fetch(`${API}/api/profile/favorite-tours/${id}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTourIds(new Set((data.favoriteTours || []).map(String)));
    } catch (err) {
      console.error('Ошибка избранного (туры):', err);
    }
  }, [tourIds, requireAuth]);

  const toggleService = useCallback(async (serviceId) => {
    if (!requireAuth()) return;
    const id = String(serviceId);
    const token = localStorage.getItem('token');
    const isFavorite = serviceIds.has(id);

    try {
      const res = await fetch(`${API}/api/profile/favorite-services/${id}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setServiceIds(new Set((data.favoriteServices || []).map(String)));
    } catch (err) {
      console.error('Ошибка избранного (услуги):', err);
    }
  }, [serviceIds, requireAuth]);

  const value = useMemo(() => ({
    itemIds,
    productIds,
    tourIds,
    serviceIds,
    isItemFavorite: (id) => itemIds.has(String(id)),
    isProductFavorite: (id) => productIds.has(String(id)),
    isTourFavorite: (id) => tourIds.has(String(id)),
    isServiceFavorite: (id) => serviceIds.has(String(id)),
    toggleItem,
    toggleProduct,
    toggleTour,
    toggleService,
    syncFromUser,
  }), [itemIds, productIds, tourIds, serviceIds, toggleItem, toggleProduct, toggleTour, toggleService, syncFromUser]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return ctx;
}
